import Dropdown, { Option } from "@/ui/components/Dropdown"
import AsyncDropdown from "@/ui/components/Dropdown/AsyncDropdown"
import Button from "@/ui/components/Button"
import Input from "@/ui/components/Input"

import AXIOS from "@/requests/_axios"

import { FormErrors, getLabelForTypeValue, typeFilterOptions, validateData } from "./helper"

import { ChangeEvent, useEffect, useMemo, useState, type FC } from "react"
import { BalanceHistoryUpdateFormData, HistoryFiltersType, type BalanceHistoryItem } from "@/types/balances"
import type { SingleValue } from "react-select"

import style from "./style.module.scss"

type Props = {
  checkedInvoices: BalanceHistoryItem[]
  checkedIds: number[]
  onClose: () => void
}

export const BalanceHistoryForm: FC<Props> = ({ checkedInvoices, checkedIds, onClose }) => {
  const [formData, setFormData] = useState<Partial<BalanceHistoryItem>>({
    operationType_value: HistoryFiltersType.Rep,
    operationType_label: "Replenishment",
    contractor_value: "",
    contractor_label: "",
    contractor_type: "",
    owner_label: "",
    owner_value: "",
    owner_type: ""
  })
  const [errors, setErrors] = useState<FormErrors>({
    contractor: false,
    owner: false
  })
  const [amount, setAmount] = useState<string>("")
  const [isUpdating, setUpdating] = useState(false)

  const isSingleInvoice = useMemo(() => {
    return checkedIds.length === 1
  }, [checkedIds])

  const handleTypeChange = (option: SingleValue<Option>) => {
    setFormData({
      ...formData,
      operationType_value: option?.value,
      operationType_label: option?.label
    })
  }

  const handleContractorChange = (option: SingleValue<Option>) => {
    setFormData({
      ...formData,
      contractor_value: option?.value,
      contractor_label: option?.label,
      contractor_type: option?.payload?.type
    })
  }

  const handleOwnerChange = (option: SingleValue<Option>) => {
    setFormData({
      ...formData,
      owner_value: option?.value,
      owner_label: option?.label,
      owner_type: option?.payload?.type
    })
  }

  const handleAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAmount(e.target.value)
  }

  const handleSave = () => {
    const validated = validateData(formData)

    if (Object.values(validated).includes(true)) {
      setErrors(validated)

      return
    }

    const payload: BalanceHistoryUpdateFormData = {
      operationType: formData.operationType_value?.toUpperCase() || "",
      contractorId: formData.contractor_value || "",
      contractorType: formData.contractor_type || "",
      ownerId: formData.owner_value || "",
      ownerType: formData.owner_type || ""
    }

    setUpdating(true)

    if (isSingleInvoice) {
      AXIOS.put(`/trinity/api/v1/bank-statement/${checkedInvoices[0]?.id}`, {
        ...payload,
        amount: Number(amount) * 100
      }).finally(() => {
        setUpdating(false)
        window.location.reload()
      })
    } else {
      const promises = checkedInvoices.map(invoice =>
        AXIOS.put(`/trinity/api/v1/bank-statement/${invoice.id}`, {
          ...payload,
          amount: invoice.dc
        })
      )

      Promise.all(promises).finally(() => {
        setUpdating(false)
        window.location.reload()
      })
    }
  }

  useEffect(() => {
    if (formData.contractor_value) {
      setErrors(prevErrors => ({
        ...prevErrors,
        contractor: false
      }))
    }

    if (formData.owner_value) {
      setErrors(prevErrors => ({
        ...prevErrors,
        owner: false
      }))
    }
  }, [formData.contractor_value, formData.owner_value])

  useEffect(() => {
    if (isSingleInvoice) {
      setAmount(String(checkedInvoices[0]?.dc / 100))
    }

    setFormData({
      ...formData,
      operationType_value: checkedInvoices[0]?.operationType?.toLowerCase() || HistoryFiltersType.Rep,
      operationType_label:
        getLabelForTypeValue(checkedInvoices[0]?.operationType?.toLowerCase() || "") || "Replenishment",
      contractor_value: String(checkedInvoices[0]?.contractorId) || "",
      contractor_label: String(checkedInvoices[0]?.contractor) || "",
      contractor_type: checkedInvoices[0]?.contractorType || "",
      owner_value: String(checkedInvoices[0]?.ownerId) || "",
      owner_label: String(checkedInvoices[0]?.owner) || "",
      owner_type: checkedInvoices[0]?.ownerType || ""
    })
  }, [isSingleInvoice, checkedInvoices])

  return (
    <section className="user-search-popup">
      <div className="container-user-search">
        <button className="close-btn-card" onClick={onClose} />
        <div className="header-user-search-popup">
          <h3 className="heading-chapter">
            Редактирование выписок: <small>{checkedIds.join(", ")}</small>
          </h3>
        </div>
        <div className={style.formContent}>
          <Dropdown
            label="Тип платежа"
            options={typeFilterOptions}
            value={{
              value: String(formData?.operationType_value),
              label: String(formData?.operationType_label)
            }}
            onChange={handleTypeChange}
            menuPlacement="auto"
            searchable
          />

          <AsyncDropdown
            label="Контрагент"
            url="/trinity/api/v1/dict/statement-contractor"
            searchKey="contractor"
            valueKey="id"
            labelKey="name"
            value={{
              value: String(formData?.contractor_value),
              label: String(formData?.contractor_label)
            }}
            onChange={handleContractorChange}
            menuPlacement="auto"
            error={errors.contractor}
            includePayload
          />

          <AsyncDropdown
            label="Владелец"
            url="/trinity/api/v1/dict/statement-contractor"
            searchKey="contractor"
            valueKey="id"
            labelKey="name"
            value={{
              value: String(formData?.owner_value),
              label: String(formData?.owner_label)
            }}
            onChange={handleOwnerChange}
            menuPlacement="auto"
            error={errors.owner}
            includePayload
          />
        </div>

        {isSingleInvoice && (
          <div className={style.amountWrapper}>
            <Input placeholder="Сумма" label="Сумма" value={amount} onChange={handleAmountChange} />
          </div>
        )}

        <div className={style.saveButton}>
          <Button loading={isUpdating} onClick={handleSave}>
            Сохранить
          </Button>
        </div>
      </div>
    </section>
  )
}
